import {
  StarRoundedIcon,
  KeyboardArrowRightRoundedIcon,
  ReportProblemIcon,
} from "./icons";
import { useDispatch, useSelector } from "react-redux";
import toPriceDisplay from "../utils/toPriceDisplay";
import React from "react";
import { getValidOrders } from "../services/fidelity";
import { setValidOrders } from "../ducks/userSlice";
import useToast from "../hooks/useToast";
import useGoTo from "../hooks/useGoTo";
import formatDateTime from "../utils/formatDateTime";

export default function FidelityButton({ className, divider }) {
  const dispatch = useDispatch();
  const goTo = useGoTo();
  const redirectToFidelityPage = () => goTo("/fidelity");

  const { fidelity } = useSelector((state) => state.shop);

  // Clear validOrders state when a store disabled the fidelity program
  React.useEffect(() => {
    if (!fidelity) {
      dispatch(setValidOrders({ validOrders: [] }));
    }
  }, [dispatch, fidelity]);

  return fidelity ? (
    <div>
      <button
        className={`fidelity-button ${className || ""}`}
        onClick={redirectToFidelityPage}
      >
        <FidelityBox />
      </button>
      {divider}
    </div>
  ) : null;
}

function FidelityBox() {
  const dispatch = useDispatch();
  const toast = useToast();
  const { id: storeId, fidelity } = useSelector((state) => state.shop);
  const { userId, userUuid, userName, validOrders } = useSelector(
    (state) => state.user,
  );
  const [isLoading, setLoading] = React.useState(true);

  const isLoggedUser = userId && userUuid;
  const hasDiscount = fidelity.ordersQuantity === validOrders.length;

  React.useEffect(() => {
    if (!isLoggedUser) {
      dispatch(setValidOrders({ validOrders: [] }));
      return;
    }
    setLoading(true);
    getValidOrders(userId, userUuid, storeId)
      .then((resp) => resp.json())
      .then((response) => {
        if (response.error) {
          throw new Error(
            response.error?.message ?? "Something wrong happened",
          );
        }

        const { pedidosValidos: validOrders } = response;
        dispatch(
          setValidOrders({
            validOrders,
          }),
        );
      })
      .catch((error) => {
        console.error(error);
        toast.toastError("Erro ao consultar seus pontos de fidelidade.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dispatch, isLoggedUser, storeId, toast, userId, userUuid]);

  if (!isLoggedUser) {
    return (
      <FidelityCard
        title="Programa de fidelidade"
        subtitle="Entre ou cadastre-se para ganhar descontos"
        showChevron
      />
    );
  }

  if (hasDiscount) {
    return (
      <FidelityCard
        highlight
        title={`${userName}, você tem ${toPriceDisplay(
          fidelity.discountValue,
        )} de desconto!`}
        subtitle="Ele entra automaticamente no seu próximo pedido."
      />
    );
  }

  const lastOrderDate =
    validOrders.length > 0
      ? new Date(validOrders[validOrders.length - 1].createdAt)
      : null;
  const nextValidDateTime = lastOrderDate
    ? lastOrderDate.setHours(lastOrderDate.getHours() + fidelity.orderInterval)
    : null;
  const waitInterval = nextValidDateTime && nextValidDateTime > new Date();
  const missing = Math.max(fidelity.ordersQuantity - validOrders.length, 0);

  return (
    <FidelityCard
      title={fidelity.description}
      subtitle={`${
        missing === 1 ? "Falta 1 pedido" : `Faltam ${missing} pedidos`
      } · mínimo de ${toPriceDisplay(fidelity.minimalPurchase)} cada`}
      showChevron
    >
      <ProgressBar
        fidelity={fidelity}
        validOrders={validOrders}
        isHidden={isLoading}
      />
      {waitInterval && (
        <p className="fidelity-card__notice">
          <ReportProblemIcon fontSize="inherit" className="me-1" />
          {`O próximo pedido vale para a fidelidade a partir de ${formatDateTime(
            nextValidDateTime,
          )}.`}
        </p>
      )}
    </FidelityCard>
  );
}

function FidelityCard({ title, subtitle, highlight, showChevron, children }) {
  return (
    <div className={`fidelity-card${highlight ? " is-highlight" : ""}`}>
      <div className="fidelity-card__row">
        <span className="fidelity-card__icon">
          <StarRoundedIcon />
        </span>
        <span className="fidelity-card__text">
          <span className="fidelity-card__title">{title}</span>
          {subtitle && (
            <span className="fidelity-card__subtitle">{subtitle}</span>
          )}
        </span>
        {showChevron && <KeyboardArrowRightRoundedIcon />}
      </div>
      {children}
    </div>
  );
}

function ProgressBar({ fidelity, validOrders, isHidden }) {
  return (
    <span
      className={`fidelity-progress${isHidden ? " is-loading" : ""}`}
      aria-label={`${validOrders.length} de ${fidelity.ordersQuantity} pedidos`}
    >
      {Array.from({ length: fidelity.ordersQuantity }, (_, index) => (
        <span
          key={index}
          className={`fidelity-progress__step${
            !isHidden && index < validOrders.length ? " is-done" : ""
          }`}
        />
      ))}
    </span>
  );
}
